import React from 'react';

import type { UUID } from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import { Download, Upload, X } from 'lucide-react';
import type { StorableIconDocument } from 'src/components';
import { AlineTooltip, StorableIcon } from 'src/components';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { formatDay } from './task-review-helpers';

const useStyles = makeStyles<void, 'actions'>()((theme, _params, classes) => ({
  actionButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    color: theme.palette.primary.main,
    padding: 6,
  },
  // Revealed on hover or keyboard focus.
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.25),
    opacity: 0,
    transition: theme.transitions.create('opacity', { duration: 120 }),
  },
  card: {
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
    '&:hover, &:focus-within': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
      borderColor: theme.palette.primary.main,
    },
    [`&:hover .${classes.actions}, &:focus-within .${classes.actions}`]: {
      opacity: 1,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 48,
    padding: theme.spacing(0.75, 0.75, 0.75, 1.25),
    transition: theme.transitions.create(['background-color', 'border-color'], {
      duration: 120,
    }),
  },
  clearButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    color: theme.appColors.iconsType,
    flexShrink: 0,
    padding: 2,
  },
  // Shares its line's two tracks, so every card on a line is as tall as the tallest one.
  item: {
    display: 'grid',
    gridRow: 'span 2',
    gridTemplateRows: 'subgrid',
  },
  label: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  name: {
    ...createClampStyles(2),
    color: theme.appColors.strongType,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
  },
  row: {
    columnGap: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    rowGap: theme.spacing(0.5),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  selected: {
    backgroundColor: theme.appColors.background,
    borderColor: theme.appColors.rule,
  },
  text: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  uploadLine: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.25),
    paddingLeft: theme.spacing(0.5),
  },
  uploadText: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const REPLACE_UPLOAD_REASON = 'Clear your upload to replace it.';

/** The viewer's upload of a document that has not been sent yet. */
export interface PendingCardUpload {
  fileName: string;
  uploadedAt: string;
  uploadId: UUID;
}

type CarouselDocument = StorableIconDocument & { name: string };

interface TaskDocumentCarouselProps {
  /** Shows the upload button. A reason keeps it visible but disabled, e.g. in a preview. */
  canUpload: boolean;
  currentDocumentId: UUID | null;
  documents: readonly CarouselDocument[];
  onClearUpload: (uploadId: UUID) => void;
  onDownload: (documentId: UUID) => void;
  onOpen: (documentId: UUID) => void;
  onUpload: (documentId: UUID) => void;
  uploadDisabledReason: string | null;
  /** The viewer's unsent upload of each document, by document id. */
  uploadsByDocumentId: ReadonlyMap<UUID, PendingCardUpload>;
  /** Drops the heading when a surrounding card already names the documents. */
  hideHeader?: boolean;
}

/** The task's documents as wrapping cards; each opens its document, downloads or takes a redline. */
export const TaskDocumentCarousel = React.memo(function TaskDocumentCarousel({
  canUpload,
  currentDocumentId,
  documents,
  hideHeader = false,
  onClearUpload,
  onDownload,
  onOpen,
  onUpload,
  uploadDisabledReason,
  uploadsByDocumentId,
}: TaskDocumentCarouselProps) {
  const { classes } = useStyles();

  return (
    <section className={classes.section}>
      {!hideHeader && (
        <Typography className={classes.label} component="h3">
          {`Documents to review (${documents.length})`}
        </Typography>
      )}
      <div aria-label="Documents to review" className={classes.row} role="list">
        {documents.map(document => (
          <TaskDocumentCard
            key={document.id}
            canUpload={canUpload}
            document={document}
            isSelected={document.id === currentDocumentId}
            upload={uploadsByDocumentId.get(document.id) ?? null}
            uploadDisabledReason={uploadDisabledReason}
            onClearUpload={onClearUpload}
            onDownload={onDownload}
            onOpen={onOpen}
            onUpload={onUpload}
          />
        ))}
      </div>
    </section>
  );
});

interface TaskDocumentCardProps extends Pick<
  TaskDocumentCarouselProps,
  'canUpload' | 'onClearUpload' | 'onDownload' | 'onOpen' | 'onUpload' | 'uploadDisabledReason'
> {
  document: CarouselDocument;
  isSelected: boolean;
  upload: PendingCardUpload | null;
}

function TaskDocumentCard({
  canUpload,
  document,
  isSelected,
  onClearUpload,
  onDownload,
  onOpen,
  onUpload,
  upload,
  uploadDisabledReason,
}: TaskDocumentCardProps): JSX.Element {
  const { classes, cx } = useStyles();
  const name = document.name || 'Untitled document';
  const uploadBlockedReason = uploadDisabledReason ?? (upload ? REPLACE_UPLOAD_REASON : null);

  return (
    <div className={classes.item} role="listitem">
      <div
        aria-current={isSelected || undefined}
        aria-label={`Open ${name}`}
        className={cx(classes.card, isSelected && classes.selected)}
        role="button"
        tabIndex={0}
        title={name}
        onClick={() => onOpen(document.id)}
        onKeyDown={event => {
          if (
            event.target === event.currentTarget &&
            (event.key === 'Enter' || event.key === ' ')
          ) {
            event.preventDefault();
            onOpen(document.id);
          }
        }}
      >
        <StorableIcon iconProps={{ height: 20, width: 20 }} storable={document} />
        <div className={classes.text}>
          <Typography className={classes.name}>{name}</Typography>
        </div>
        <TaskDocumentCardActions
          canUpload={canUpload}
          documentId={document.id}
          name={name}
          uploadBlockedReason={uploadBlockedReason}
          onDownload={onDownload}
          onUpload={onUpload}
        />
      </div>
      {upload && (
        <div className={classes.uploadLine}>
          <Typography className={classes.uploadText} title={upload.fileName}>
            {`You uploaded ${upload.fileName} on ${formatDay(upload.uploadedAt)}`}
          </Typography>
          <AlineTooltip arrow placement="top" title="Remove your upload">
            <IconButton
              aria-label={`Remove your upload of ${name}`}
              className={classes.clearButton}
              size="small"
              onClick={() => onClearUpload(upload.uploadId)}
            >
              <X size={14} />
            </IconButton>
          </AlineTooltip>
        </div>
      )}
    </div>
  );
}

function TaskDocumentCardActions({
  canUpload,
  documentId,
  name,
  onDownload,
  onUpload,
  uploadBlockedReason,
}: Pick<TaskDocumentCardProps, 'canUpload' | 'onDownload' | 'onUpload'> & {
  documentId: UUID;
  name: string;
  uploadBlockedReason: string | null;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.actions} onClick={event => event.stopPropagation()}>
      <AlineTooltip arrow placement="top" title="Download">
        <IconButton
          aria-label={`Download ${name}`}
          className={classes.actionButton}
          size="small"
          onClick={() => onDownload(documentId)}
        >
          <Download size={16} />
        </IconButton>
      </AlineTooltip>
      {canUpload && (
        <AlineTooltip arrow placement="top" title={uploadBlockedReason ?? 'Upload a file'}>
          <span>
            <IconButton
              aria-label={`Upload a new version of ${name}`}
              className={classes.actionButton}
              disabled={Boolean(uploadBlockedReason)}
              size="small"
              onClick={() => onUpload(documentId)}
            >
              <Upload size={16} />
            </IconButton>
          </span>
        </AlineTooltip>
      )}
    </div>
  );
}
